import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import { useEffect } from "react";

const PublicShell = ({ title, children, testid }) => {
  useEffect(() => {
    if (title) document.title = `${title} · RetainerTracker`;
  }, [title]);

  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#1F2326] flex flex-col">
      <PromoBanner />
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F8F6F1]/85 border-b border-[#E4DFD2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo">
            <Logo size={28} />
          </Link>
          <Link
            to="/"
            data-testid="legal-back"
            className="inline-flex items-center gap-1 text-sm text-[#69747C] hover:text-[#1F2326]"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
      </header>

      <main className="flex-1" data-testid={testid}>
        <div className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
          <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">
            RetainerTracker
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-[#1F2326] mt-2">
            {title}
          </h1>
          <div className="mt-8 prose prose-neutral max-w-none text-[#1F2326] text-base leading-relaxed">
            {children}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export const Privacy = () => (
  <PublicShell title="Privacy Policy" testid="privacy-page">
    <p>Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">What we collect</h2>
    <p>
      When you sign up for RetainerTracker, we collect your email address and basic
      profile details from your Google account (name, picture). When you use the app,
      we store the retainer data you enter: client names, fees, hours, and notes.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">How we use it</h2>
    <p>
      We use this information to operate your account, show you your dashboard,
      send service emails, and improve the product. We do not sell your data.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Payment data</h2>
    <p>
      Payments are processed by Stripe. We do not store your full card number.
      We retain a record of the plan you chose and the transaction status.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Cookies and analytics</h2>
    <p>
      We use a session cookie to keep you signed in. We track basic usage events
      (page views, signups, key actions) to understand product use. No third-party
      ad tracking.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Your data</h2>
    <p>
      You can request a copy or deletion of your data by emailing{" "}
      <a className="text-[#B89B5E]" href="mailto:support@retainertracker.com">
        support@retainertracker.com
      </a>
      . We will respond within 30 days.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Contact</h2>
    <p>
      Questions? Email{" "}
      <a className="text-[#B89B5E]" href="mailto:support@retainertracker.com">
        support@retainertracker.com
      </a>
      .
    </p>
  </PublicShell>
);

export const Terms = () => (
  <PublicShell title="Terms of Use" testid="terms-page">
    <p>Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Your account</h2>
    <p>
      You agree to provide accurate information and to keep your sign-in credentials
      private. You are responsible for activity in your account.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Acceptable use</h2>
    <p>
      Do not use RetainerTracker to break the law, send spam, or attempt to access
      other users' data. We may suspend accounts that abuse the service.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Trial and billing</h2>
    <p>
      New accounts get a 14-day free trial. After that, choose a paid plan to continue.
      Subscriptions are billed monthly through Stripe and renew automatically. Cancel
      any time by contacting support.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Service availability</h2>
    <p>
      We aim to keep RetainerTracker available, but we do not guarantee uninterrupted
      service. The product is provided "as is" with no warranties.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Liability</h2>
    <p>
      Our total liability for any claim is limited to the amount you paid us in the
      last twelve months. We are not liable for indirect or consequential damages.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Changes</h2>
    <p>
      We may update these terms. If changes are material, we will give notice in the
      app or by email.
    </p>

    <h2 className="font-display text-2xl font-semibold mt-8 mb-3">Contact</h2>
    <p>
      Email{" "}
      <a className="text-[#B89B5E]" href="mailto:support@retainertracker.com">
        support@retainertracker.com
      </a>
      .
    </p>
  </PublicShell>
);

export const Contact = () => (
  <PublicShell title="Contact us" testid="contact-page">
    <p>
      We read every message and aim to reply within one business day.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
      <a
        href="mailto:support@retainertracker.com"
        className="block bg-white border border-[#E4DFD2] rounded-xl p-6 hover:border-[#B89B5E] transition-colors"
        data-testid="contact-support-card"
      >
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
          Support
        </div>
        <div className="font-display text-xl font-semibold text-[#1F2326] mt-1">
          support@retainertracker.com
        </div>
        <p className="text-sm text-[#69747C] mt-2">
          Bug reports, account help, billing questions.
        </p>
      </a>

      <a
        href="mailto:hello@retainertracker.com"
        className="block bg-white border border-[#E4DFD2] rounded-xl p-6 hover:border-[#B89B5E] transition-colors"
        data-testid="contact-hello-card"
      >
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
          General
        </div>
        <div className="font-display text-xl font-semibold text-[#1F2326] mt-1">
          hello@retainertracker.com
        </div>
        <p className="text-sm text-[#69747C] mt-2">
          Feedback, partnerships, press.
        </p>
      </a>
    </div>
  </PublicShell>
);
